import React, { useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Checkbox,
    Container,
    ContentLayout,
    FormField,
    Header,
    SpaceBetween,
    Table,
    Textarea,
} from '@cloudscape-design/components';
import apiAxios from '../../client/apiClient';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
    BackfillTickerResult,
    ForceBackfillResponse,
} from '../../api/models';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Mirror StockPricesBackfillService.MAX_TICKERS_PER_REQUEST so we can disable
// the submit button BEFORE the network round-trip when the user clearly went
// over the cap. The cap is sized to fit inside the rest-service Lambda's 29s
// timeout (Polygon rate-limits at ~1.1s/call). The server still enforces this
// — this is purely UX.
const MAX_TICKERS_PER_REQUEST = 10;

/**
 * Parse a comma- and/or whitespace-separated list of tickers into a deduplicated,
 * upper-cased array. Mirrors the server-side normalisation so the preview matches
 * what the backend will actually process.
 */
function parseTickers(raw: string): string[] {
    if (!raw) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const tok of raw.split(/[\s,]+/)) {
        const up = tok.trim().toUpperCase();
        if (up && !seen.has(up)) {
            seen.add(up);
            out.push(up);
        }
    }
    return out;
}

function statusBadge(status?: string): React.ReactNode {
    switch (status) {
        case 'POPULATED':
            return <Badge color="green">POPULATED</Badge>;
        case 'SKIPPED_HAS_HISTORY':
            return <Badge color="grey">SKIPPED</Badge>;
        case 'NOT_FOUND':
            return <Badge color="blue">NOT FOUND</Badge>;
        case 'TRANSIENT_ERROR':
            return <Badge color="severity-medium">TRANSIENT</Badge>;
        case 'ERROR':
            return <Badge color="red">ERROR</Badge>;
        default:
            return <Badge>{status ?? '—'}</Badge>;
    }
}

export const StockPricesAdminPage: React.FC = () => {
    useDocumentTitle('Stock Prices — Admin');

    const [tickersRaw, setTickersRaw] = useState('');
    const [force, setForce] = useState(true);  // sensible default for the headline use case
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [response, setResponse] = useState<ForceBackfillResponse | null>(null);

    const parsedTickers = useMemo(() => parseTickers(tickersRaw), [tickersRaw]);
    const overLimit = parsedTickers.length > MAX_TICKERS_PER_REQUEST;
    const submitDisabled = loading || parsedTickers.length === 0 || overLimit;

    const handleBackfill = async () => {
        setError(null);
        setResponse(null);
        setLoading(true);
        try {
            const resp = await apiAxios.post<ForceBackfillResponse>(
                `${API_BASE}/api/alpha-analysis/backfill-stock-prices/tickers`,
                { tickers: parsedTickers, force },
            );
            setResponse(resp.data);
            if (resp.data?.error) {
                // Server returned 200 OK with an error field — surface it
                setError(resp.data.error);
            }
        } catch (err: any) {
            // 4xx/5xx — axios throws. Server still returns a ForceBackfillResponse
            // body with an error field in that case; prefer that if present.
            const data = err.response?.data as ForceBackfillResponse | undefined;
            const msg = data?.error || err.response?.data?.error || err.message || 'Unknown error';
            setError(msg);
            if (data?.results) setResponse(data);
        } finally {
            setLoading(false);
        }
    };

    const totalBars = response?.totalBarsWritten ?? 0;
    const results: BackfillTickerResult[] = response?.results ?? [];

    return (
        <ContentLayout header={<Header variant="h1">Stock Prices</Header>}>
            <SpaceBetween size="l">
                <Container
                    header={
                        <Header
                            variant="h2"
                            description={
                                'Fetches 5 years of daily OHLC from Polygon and writes to the StockPrices table. ' +
                                'Use this when a ticker is missing or has only a tiny recent sliver of history. ' +
                                'Enable "Force re-fetch" to overwrite existing data; otherwise tickers that already ' +
                                'have history older than 60 days will be skipped. Max 10 tickers per call (Lambda timeout).'
                            }
                        >
                            Force Backfill Tickers
                        </Header>
                    }
                >
                    <SpaceBetween size="m">
                        {error && (
                            <Alert type="error" dismissible onDismiss={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        <FormField
                            label="Tickers"
                            description="Comma- or whitespace-separated. Case-insensitive. e.g. MU, MXL, TTMI, AMD"
                            constraintText={
                                parsedTickers.length === 0
                                    ? 'Enter at least one ticker.'
                                    : overLimit
                                    ? `Too many tickers (${parsedTickers.length}); max is ${MAX_TICKERS_PER_REQUEST}.`
                                    : `${parsedTickers.length} ticker(s) parsed: ${parsedTickers.join(', ')}`
                            }
                            errorText={overLimit ? `Reduce to ${MAX_TICKERS_PER_REQUEST} or fewer.` : undefined}
                        >
                            <Textarea
                                value={tickersRaw}
                                onChange={({ detail }) => setTickersRaw(detail.value)}
                                placeholder="MU, MXL, TTMI, AMD"
                                rows={3}
                            />
                        </FormField>

                        <FormField>
                            <Checkbox
                                checked={force}
                                onChange={({ detail }) => setForce(detail.checked)}
                                description="When enabled, bypasses the 'oldest row >60 days old' check and always re-fetches from Polygon. Recommended for ad-hoc repair."
                            >
                                Force re-fetch (ignore existing history)
                            </Checkbox>
                        </FormField>

                        <Box>
                            <Button
                                variant="primary"
                                loading={loading}
                                disabled={submitDisabled}
                                onClick={handleBackfill}
                            >
                                Backfill
                            </Button>
                        </Box>
                    </SpaceBetween>
                </Container>

                {response && (
                    <Container
                        header={
                            <Header
                                variant="h2"
                                counter={`(${results.length} ticker${results.length === 1 ? '' : 's'}, ${totalBars.toLocaleString()} bars written)`}
                            >
                                Results
                            </Header>
                        }
                    >
                        <Table
                            variant="embedded"
                            items={results}
                            trackBy={(r) => r.ticker ?? ''}
                            empty={<Box textAlign="center" color="inherit">No results.</Box>}
                            columnDefinitions={[
                                {
                                    id: 'ticker',
                                    header: 'Ticker',
                                    cell: (r) => <strong>{r.ticker}</strong>,
                                    width: 110,
                                },
                                {
                                    id: 'status',
                                    header: 'Status',
                                    cell: (r) => statusBadge(r.status),
                                    width: 160,
                                },
                                {
                                    id: 'bars',
                                    header: 'Bars written',
                                    cell: (r) => (r.barsWritten ?? 0).toLocaleString(),
                                    width: 140,
                                },
                                {
                                    id: 'message',
                                    header: 'Message',
                                    cell: (r) => r.message ?? '—',
                                },
                            ]}
                        />
                    </Container>
                )}
            </SpaceBetween>
        </ContentLayout>
    );
};
